import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supabase } from '@shared/supabase/client';
import { Button, Input, InstallAppButton, Footer } from '@shared/ui';
import { LanguageSwitcher } from '@shared/i18n/LanguageSwitcher';

type Mode = 'password' | 'phone' | 'forgot' | 'reset' | 'signup';
type AccountType = 'advertiser' | 'provider';

const PHONE_PREFIX = '+996';

// Phone auth is hidden for now — email is the only sign-in/sign-up method. Flip to
// re-enable the email/phone switcher and the phone OTP flow.
const SHOW_PHONE_AUTH = false;

export default function Login() {
  const { t } = useTranslation(['public', 'common']);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialMode: Mode = searchParams.get('mode') === 'signup' ? 'signup' : 'password';
  const initialType: AccountType = searchParams.get('type') === 'provider' ? 'provider' : 'advertiser';
  const [mode, setMode] = useState<Mode>(initialMode);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [phoneStep, setPhoneStep] = useState<'enter' | 'verify'>('enter');
  const [signupStep, setSignupStep] = useState<'form' | 'verify'>('form');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  // Signup-specific state
  const [displayName, setDisplayName] = useState('');
  const [igHandle, setIgHandle] = useState('');   // creator: IG handle to claim + verify ownership
  const [consent, setConsent] = useState(false); // явное согласие — не предотмечено
  const [accountType, setAccountType] = useState<AccountType>(initialType);

  const reset = () => { setError(null); setMessage(null); };

  const goToLogin = () => { setMode('password'); setSignupStep('form'); setOtp(''); reset(); };

  // Supabase Auth answers in English with fixed phrases; show them in the UI language.
  const authErr = (m: string): string => {
    const x = m.toLowerCase();
    if (x.includes('invalid login credentials')) return t('public:auth.errInvalidCredentials', { defaultValue: 'Неверный email или пароль.' });
    if (x.includes('email not confirmed')) return t('public:auth.errEmailNotConfirmed', { defaultValue: 'Email не подтверждён — введите код из письма.' });
    if (x.includes('expired') || (x.includes('invalid') && x.includes('token'))) return t('public:auth.errOtpInvalid', { defaultValue: 'Код неверный или устарел — запросите новый.' });
    if (x.includes('already registered')) return t('public:auth.emailAlreadyRegistered', { defaultValue: 'Этот email уже зарегистрирован — войдите в аккаунт.' });
    if (x.includes('rate limit') || x.includes('security purposes')) return t('public:auth.errRateLimit', { defaultValue: 'Слишком много попыток — подождите минуту и попробуйте снова.' });
    if (x.includes('at least 6')) return t('public:auth.passwordTooShort');
    return m;
  };

  // Re-send the 6-digit sign-up code. Supabase throttles this (~60s), so the button
  // counts down instead of failing silently on a quick second tap.
  const [resendIn, setResendIn] = useState(0);
  useEffect(() => {
    if (resendIn <= 0) return;
    const id = setTimeout(() => setResendIn((n) => n - 1), 1000);
    return () => clearTimeout(id);
  }, [resendIn]);
  const resendCode = async () => {
    reset();
    const { error: err } = await supabase.auth.resend({ type: 'signup', email: email.trim() });
    if (err) { setError(authErr(err.message)); return; }
    setResendIn(60);
    setMessage(t('public:auth.emailCodeSent'));
  };

  // Resolve role and redirect to the appropriate panel after sign-in
  const redirectByRole = async (userId?: string) => {
    if (!userId) { navigate('/customer'); return; }
    const { data: rows } = await supabase.from('user_roles').select('role').eq('user_id', userId);
    const RANK: Record<string, number> = { public: 0, customer: 1, creator: 1, field_agent: 1, admin: 3 };
    const role = (rows ?? []).map(r => r.role).sort((a, b) => (RANK[b] ?? 0) - (RANK[a] ?? 0))[0] ?? 'customer';
    if (role === 'admin') navigate('/admin');
    else if (role === 'creator') navigate('/provider');
    else if (role === 'field_agent') navigate('/agent');   // was falling through to /customer
    else navigate('/customer');
  };

  const handlePassword = async () => {
    reset();
    if (!email.trim() || !password) return setError(t('public:auth.missingFields'));
    setLoading(true);
    const { data, error: err } = await supabase.auth.signInWithPassword({ email: email.trim(), password });
    setLoading(false);
    if (err && err.message.toLowerCase().includes('email not confirmed')) {
      await supabase.auth.resend({ type: 'signup', email: email.trim() });
      setOtp(''); setMode('signup'); setSignupStep('verify'); setResendIn(60);
      setMessage(t('public:auth.errEmailNotConfirmed', { defaultValue: 'Email не подтверждён — введите код из письма.' }));
      return;
    }
    if (err) { setError(authErr(err.message)); return; }
    await redirectByRole(data.user?.id);
  };

  // Signup: create the account with a password, then confirm via a 6-digit code
  // (the "Confirm signup" email template must emit {{ .Token }}, not a link).
  const handleSignup = async () => {
    reset();
    if (!email.trim() || !password) return setError(t('public:auth.missingFields'));
    if (password.length < 6) return setError(t('public:auth.passwordTooShort'));
    if (!displayName.trim()) return setError(t('public:auth.nameRequired'));

    setLoading(true);

    // Stash the creator's IG handle across the email-verify step so the claim screen
    // can auto-start ownership verification (creator_claim_start → ADAMAR code) once
    // they're authenticated.
    const claimHandle = accountType === 'provider' ? igHandle.trim().replace(/^@/, '') : '';
    if (claimHandle) { try { localStorage.setItem('adamar_claim_handle', claimHandle); } catch { /* ignore */ } }

    const { data, error: err } = await supabase.auth.signUp({
      email: email.trim(),
      password,
      // Creators (accountType='provider') self-serve the `creator` role and land straight in
      // the creator app → claim flow. Advertisers pass no role → precustomer → admin approval.
      // The trigger only honors 'creator'; any other value falls back to precustomer.
      options: { data: { display_name: displayName.trim(), ...(accountType === 'provider' ? { role: 'creator' } : {}) } },
    });

    setLoading(false);

    if (err) { setError(authErr(err.message)); return; }
    // Supabase anti-enumeration: signing up an already-registered email returns a user
    // with an EMPTY identities array and sends NO code. Don't send the user to the OTP
    // screen — switch to sign-in and tell them the email already exists.
    if (data.user && (data.user.identities?.length ?? 0) === 0) {
      setMode('password');
      setSignupStep('form');
      setMessage(t('public:auth.emailAlreadyRegistered', { defaultValue: 'Этот email уже зарегистрирован — войдите в аккаунт.' }));
      return;
    }
    setOtp('');
    setSignupStep('verify');
    setResendIn(60);
    setMessage(t('public:auth.emailCodeSent'));
  };

  const handleSignupVerify = async () => {
    reset();
    if (otp.length < 6) return setError(t('public:auth.otpInvalid'));
    setLoading(true);
    const { data, error: err } = await supabase.auth.verifyOtp({
      email: email.trim(),
      token: otp,
      type: 'signup',
    });
    setLoading(false);
    if (err) { setError(authErr(err.message)); return; }
    await redirectByRole(data.user?.id);
  };

  // Forgot password: email a recovery code (the "Reset Password" template must emit {{ .Token }}).
  const handleForgot = async () => {
    reset();
    if (!email.trim()) return setError(t('public:auth.emailRequired'));
    setLoading(true);
    const { error: err } = await supabase.auth.resetPasswordForEmail(email.trim());
    setLoading(false);
    if (err) { setError(err.message); return; }
    setOtp('');
    setNewPassword('');
    setMode('reset');
    setMessage(t('public:auth.emailCodeSent'));
  };

  // Reset: verify the recovery code (opens a session), then set the new password.
  const handleReset = async () => {
    reset();
    if (otp.length < 6) return setError(t('public:auth.otpInvalid'));
    if (newPassword.length < 6) return setError(t('public:auth.passwordTooShort'));
    setLoading(true);
    const { error: vErr } = await supabase.auth.verifyOtp({
      email: email.trim(),
      token: otp,
      type: 'recovery',
    });
    if (vErr) { setLoading(false); setError(vErr.message); return; }
    const { error: uErr } = await supabase.auth.updateUser({ password: newPassword });
    if (uErr) { setLoading(false); setError(uErr.message); return; }
    // Password is set and a valid session is active → hard-navigate to the app root so
    // AuthContext re-initializes cleanly from the persisted session and routes by role.
    // A react-router navigate() (or signOut + mode switch) here races the async session
    // pickup and leaves the button spinning — the user had to refresh manually.
    window.location.assign('/');
  };

  const handlePhoneSend = async () => {
    reset();
    const digits = phone.replace(/\D/g, '').slice(0, 9);
    if (digits.length < 9) return setError(t('public:auth.phoneInvalid'));
    setPhone(digits);
    setLoading(true);
    const { error: err } = await supabase.auth.signInWithOtp({ phone: PHONE_PREFIX + digits });
    setLoading(false);
    if (err) setError(err.message);
    else { setPhoneStep('verify'); setMessage(t('public:auth.otpSent')); }
  };

  const handlePhoneVerify = async () => {
    reset();
    if (otp.length < 6) return setError(t('public:auth.otpInvalid'));
    setLoading(true);
    const { error: err } = await supabase.auth.verifyOtp({
      phone: PHONE_PREFIX + phone,
      token: otp,
      type: 'sms',
    });
    setLoading(false);
    if (err) setError(err.message);
    else navigate('/customer');
  };

  return (
    <div className="adamar-auth">
      <div className="auth-card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <Link to="/" style={{ fontSize: 12, color: 'var(--muted)' }}>← {t('common:common.back')}</Link>
          <LanguageSwitcher />
        </div>

        <h1>Ada<span className="grad">Mar</span></h1>
        <p className="auth-sub">
          {mode === 'signup'
            ? t('public:auth.signupSubtitle', { defaultValue: 'Create your AdaMar account' })
            : t('public:auth.subtitle')}
        </p>

        {SHOW_PHONE_AUTH && (mode === 'password' || mode === 'phone') && (
          <div className="auth-mode-switch">
            <button
              className={`auth-mode-btn ${mode === 'password' ? 'active' : ''}`}
              onClick={() => { setMode('password'); reset(); }}
            >
              {t('public:auth.email')}
            </button>
            <button
              className={`auth-mode-btn ${mode === 'phone' ? 'active' : ''}`}
              onClick={() => { setMode('phone'); reset(); setPhoneStep('enter'); }}
            >
              {t('public:auth.phone')}
            </button>
          </div>
        )}

        {mode === 'password' && (
          <form
            style={{ display: 'flex', flexDirection: 'column', gap: 12 }}
            onSubmit={(e) => { e.preventDefault(); handlePassword(); }}
          >
            <Input label={t('public:auth.emailLabel')} type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="email" name="email" />
            <Input label={t('public:auth.passwordLabel')} type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="current-password" name="password" />
            <Button type="submit" loading={loading}>{t('public:auth.signIn')}</Button>
            <Button type="button" variant="ghost" onClick={() => { setMode('forgot'); setOtp(''); reset(); }}>{t('public:auth.forgot')}</Button>
            <div style={{ textAlign: 'center', fontSize: 13, color: 'var(--muted)', marginTop: 4 }}>
              {t('public:auth.noAccount', { defaultValue: "Don't have an account?" })}{' '}
              <button
                type="button"
                onClick={() => { setMode('signup'); setSignupStep('form'); setOtp(''); reset(); }}
                style={{ background: 'none', border: 0, padding: 0, color: 'var(--primary, #6c5ce7)', cursor: 'pointer', textDecoration: 'underline' }}
              >
                {t('public:auth.signUp', { defaultValue: 'Sign up' })}
              </button>
            </div>
          </form>
        )}

        {mode === 'signup' && signupStep === 'form' && (
          <form
            style={{ display: 'flex', flexDirection: 'column', gap: 12 }}
            onSubmit={(e) => { e.preventDefault(); handleSignup(); }}
          >
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                type="button"
                className={`auth-mode-btn ${accountType === 'advertiser' ? 'active' : ''}`}
                style={{ flex: 1 }}
                onClick={() => setAccountType('advertiser')}
              >
                {t('public:auth.typeAdvertiser', { defaultValue: 'Advertiser' })}
              </button>
              <button
                type="button"
                className={`auth-mode-btn ${accountType === 'provider' ? 'active' : ''}`}
                style={{ flex: 1 }}
                onClick={() => setAccountType('provider')}
              >
                {t('public:auth.typeProvider', { defaultValue: 'Creator' })}
              </button>
            </div>
            <Input
              label={t('public:auth.nameLabel', { defaultValue: 'Your name' })}
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              autoComplete="name"
              name="display_name"
            />
            {accountType === 'provider' && (
              <div>
                <Input
                  label={t('public:auth.igHandleLabel', { defaultValue: 'Instagram handle' })}
                  value={igHandle}
                  onChange={(e) => setIgHandle(e.target.value)}
                  autoComplete="off"
                  name="ig_handle"
                  placeholder="@yourhandle"
                />
                <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 4 }}>
                  {t('public:auth.igHandleHint', { defaultValue: 'We’ll find your stats and ask you to confirm you own it after sign-up.' })}
                </div>
              </div>
            )}
            <Input label={t('public:auth.emailLabel')} type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="email" name="email" />
            <Input label={t('public:auth.passwordLabel')} type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="new-password" name="password" />
            {/* Явное согласие (закон КР о перс. данных): чекбокс НЕ предотмечен */}
            <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: 13, color: 'var(--muted)', cursor: 'pointer' }}>
              <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)}
                     style={{ marginTop: 2, accentColor: 'var(--accent)' }} />
              <span>
                {t('public:auth.consentPrefix', { defaultValue: 'Принимаю ' })}
                <a href="/terms" target="_blank" rel="noreferrer" style={{ color: 'var(--accent, #4a9eff)' }}>{t('public:auth.consentTerms', { defaultValue: 'условия соглашения' })}</a>
                {t('public:auth.consentMid', { defaultValue: ' и даю согласие на обработку персональных данных, включая трансграничную передачу (' })}
                <a href="/privacy" target="_blank" rel="noreferrer" style={{ color: 'var(--accent, #4a9eff)' }}>{t('public:auth.consentPrivacy', { defaultValue: 'политика конфиденциальности' })}</a>
                {t('public:auth.consentSuffix', { defaultValue: ')' })}
              </span>
            </label>
            <Button type="submit" loading={loading} disabled={!consent}>
              {t('public:auth.signUp', { defaultValue: 'Sign up' })}
            </Button>
            <div style={{ textAlign: 'center', fontSize: 13, color: 'var(--muted)' }}>
              {t('public:auth.haveAccount', { defaultValue: 'Already have an account?' })}{' '}
              <button
                type="button"
                onClick={goToLogin}
                style={{ background: 'none', border: 0, padding: 0, color: 'var(--primary, #6c5ce7)', cursor: 'pointer', textDecoration: 'underline' }}
              >
                {t('public:auth.signIn')}
              </button>
            </div>
          </form>
        )}

        {mode === 'signup' && signupStep === 'verify' && (
          <form
            style={{ display: 'flex', flexDirection: 'column', gap: 12 }}
            onSubmit={(e) => { e.preventDefault(); handleSignupVerify(); }}
          >
            <p style={{ fontSize: 13, color: 'var(--muted)', margin: 0 }}>
              {t('public:auth.emailCodePrompt', { email })}
            </p>
            <Input
              label={t('public:auth.emailCodeLabel')}
              value={otp}
              onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
              inputMode="numeric"
              placeholder="123456"
              autoFocus
            />
            <Button type="submit" loading={loading}>{t('public:auth.verifyOtp')}</Button>
            <Button type="button" variant="ghost" disabled={resendIn > 0} onClick={resendCode}>
              {resendIn > 0
                ? t('public:auth.resendIn', { s: resendIn, defaultValue: 'Отправить код ещё раз ({{s}} с)' })
                : t('public:auth.resendCode', { defaultValue: 'Отправить код ещё раз' })}
            </Button>
            <Button type="button" variant="ghost" onClick={() => { setSignupStep('form'); setOtp(''); reset(); }}>{t('common:common.back')}</Button>
          </form>
        )}

        {mode === 'forgot' && (
          <form
            style={{ display: 'flex', flexDirection: 'column', gap: 12 }}
            onSubmit={(e) => { e.preventDefault(); handleForgot(); }}
          >
            <p style={{ fontSize: 13, color: 'var(--muted)', margin: 0 }}>
              {t('public:auth.forgotPrompt')}
            </p>
            <Input label={t('public:auth.emailLabel')} type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="email" name="email" />
            <Button type="submit" loading={loading}>{t('public:auth.sendOtp')}</Button>
            <Button type="button" variant="ghost" onClick={goToLogin}>{t('common:common.back')}</Button>
          </form>
        )}

        {mode === 'reset' && (
          <form
            style={{ display: 'flex', flexDirection: 'column', gap: 12 }}
            onSubmit={(e) => { e.preventDefault(); handleReset(); }}
          >
            <p style={{ fontSize: 13, color: 'var(--muted)', margin: 0 }}>
              {t('public:auth.resetPrompt')}
            </p>
            <Input
              label={t('public:auth.emailCodeLabel')}
              value={otp}
              onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
              inputMode="numeric"
              placeholder="123456"
              autoFocus
            />
            <Input label={t('public:auth.newPasswordLabel')} type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} autoComplete="new-password" />
            <Button type="submit" loading={loading}>{t('public:auth.setPassword')}</Button>
            <Button type="button" variant="ghost" onClick={goToLogin}>{t('common:common.back')}</Button>
          </form>
        )}

        {mode === 'phone' && phoneStep === 'enter' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <Input
              label={t('public:auth.phoneLabel')}
              value={phone}
              onChange={(e) => setPhone(e.target.value.replace(/\D/g, '').slice(0, 9))}
              placeholder="700123456"
              inputMode="numeric"
            />
            <span style={{ fontSize: 12, color: 'var(--muted)' }}>{PHONE_PREFIX} {phone}</span>
            <Button loading={loading} onClick={handlePhoneSend}>{t('public:auth.sendOtp')}</Button>
          </div>
        )}

        {mode === 'phone' && phoneStep === 'verify' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <Input
              label={t('public:auth.otpLabel')}
              value={otp}
              onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
              inputMode="numeric"
              placeholder="123456"
            />
            <Button loading={loading} onClick={handlePhoneVerify}>{t('public:auth.verifyOtp')}</Button>
            <Button variant="ghost" onClick={() => { setPhoneStep('enter'); reset(); }}>{t('common:common.back')}</Button>
          </div>
        )}

        {error && <div className="auth-msg-err">{error}</div>}
        {message && <div className="auth-msg-ok">{message}</div>}

        <InstallAppButton style={{ marginTop: 18, width: '100%' }} />
      </div>
      <div style={{ width: '100%' }}><Footer /></div>
    </div>
  );
}
